import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { employeeAPI, settingsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { DataTable, SortableHeader, DataTableViewOptions, useDataTable } from '../../components/ui/data-table';
import { Checkbox } from '../../components/ui/checkbox';
import { MultiSelect } from '../../components/ui/multi-select';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import * as XLSX from 'xlsx';
import { Plus, Search, Loader2, Eye, Edit, UserX, RefreshCcw, Upload, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import BulkUploadModal from './BulkUploadModal';
import { cn } from '../../lib/utils';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import ModuleDisabled from '../../components/shared/ModuleDisabled';


const EXPORT_FIELDS = [
  { id: 'employee_code', label: 'Employee Code', category: 'Basic' },
  { id: 'first_name', label: 'First Name', category: 'Basic' },
  { id: 'last_name', label: 'Last Name', category: 'Basic' },
  { id: 'email', label: 'Email', category: 'Basic' },
  { id: 'phone', label: 'Phone', category: 'Basic' },
  { id: 'date_of_joining', label: 'Date of Joining', category: 'Employment' },
  { id: 'department', label: 'Department', category: 'Employment' },
  { id: 'designation', label: 'Designation', category: 'Employment' },
  { id: 'employment_type', label: 'Employment Type', category: 'Employment' },
  { id: 'work_locations', label: 'Work Locations', category: 'Employment' },
  { id: 'status', label: 'Status', category: 'Employment' },
  { id: 'date_of_birth', label: 'Date of Birth', category: 'Personal' },
  { id: 'gender', label: 'Gender', category: 'Personal' },
  { id: 'personal_email', label: 'Personal Email', category: 'Personal' },
  { id: 'blood_group', label: 'Blood Group', category: 'Personal' },
  { id: 'emergency_contact', label: 'Emergency Contact', category: 'Personal' },
  { id: 'address', label: 'Address', category: 'Personal' },
  { id: 'aadhaar', label: 'Aadhaar', category: 'Financial' },
  { id: 'pan', label: 'PAN', category: 'Financial' },
  { id: 'pf_account_number', label: 'PF Account No', category: 'Financial' },
  { id: 'uan', label: 'UAN', category: 'Financial' },
  { id: 'bank_name', label: 'Bank Name', category: 'Financial' },
  { id: 'account_number', label: 'Account Number', category: 'Financial' },
  { id: 'ifsc_code', label: 'IFSC Code', category: 'Financial' },
  { id: 'branch', label: 'Branch', category: 'Financial' },
];

const Employees = () => {
  const { user } = useAuth();
  const { dialogProps, confirm } = useConfirmation();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [departments, setDepartments] = useState([]); // Filter departments
  const [deptOptions, setDeptOptions] = useState([]); // Settings departments
  const [desgOptions, setDesgOptions] = useState([]); // Settings designations
  const [locations, setLocations] = useState([]); // Settings locations
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState(EXPORT_FIELDS.filter(f => f.category === 'Basic').map(f => f.id));
  const [createLoading, setCreateLoading] = useState(false);
  const { rowSelection, setRowSelection, columnVisibility, setColumnVisibility } = useDataTable();
  const [formData, setFormData] = useState({
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_joining: '',
    department: '',
    designation: '',
    employment_type: 'FULL_TIME',
    work_locations: [],
    user_password: '',
  });

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, department]);

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getEmployees({
        page,
        page_size: 20,
        search: search || undefined,
        department: department || undefined,
      });
      setEmployees(response.data.employees);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 403) {
        setError(error.response.data.detail);
      } else {
        toast.error('Failed to fetch employees');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await employeeAPI.getDepartments();
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const [deptRes, desgRes, locRes] = await Promise.all([
        settingsAPI.getDepartments(),
        settingsAPI.getDesignations(),
        settingsAPI.getLocations()
      ]);
      setDeptOptions(deptRes.data);
      setDesgOptions(desgRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEmployees();
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      await employeeAPI.createEmployee({
        ...formData,
        company_id: user.company_id,
      });
      toast.success('Employee created successfully');
      setIsCreateOpen(false);
      setFormData({
        employee_code: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_joining: '',
        department: '',
        designation: '',
        employment_type: 'FULL_TIME',
        work_locations: [],
        user_password: '',
      });
      fetchEmployees();
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeactivate = async (employeeId) => {
    const confirmed = await confirm({
      title: 'Deactivate Employee',
      description: 'Are you sure you want to deactivate this employee? They will no longer have access to the system.',
      confirmText: 'Deactivate',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await employeeAPI.deactivateEmployee(employeeId);
      toast.success('Employee deactivated');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to deactivate employee');
    }
  };

  const handleReactivate = async (employeeId) => {
    const confirmed = await confirm({
      title: 'Reactivate Employee',
      description: 'Are you sure you want to reactivate this employee? They will regain access to the system.',
      confirmText: 'Reactivate',
    });
    if (!confirmed) return;

    try {
      await employeeAPI.reactivateEmployee(employeeId);
      toast.success('Employee reactivated');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to reactivate employee');
    }
  };

  const handleDownloadExcel = () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) {
      toast.error('Select employees to export');
      return;
    }
    setIsExportOpen(true);
  };

  const performExport = () => {
    const selectedEmployees = employees.filter((_, index) => rowSelection[index]);

    if (selectedEmployees.length === 0) {
      toast.error('No employee data found for selection');
      return;
    }

    if (selectedExportFields.length === 0) {
      toast.error('Select at least one field to export');
      return;
    }

    const dataToExport = selectedEmployees.map(emp => {
      const row = {};
      selectedExportFields.forEach(fieldId => {
        const field = EXPORT_FIELDS.find(f => f.id === fieldId);
        let value = emp[fieldId];

        // Handle nested bank details
        if (['bank_name', 'account_number', 'ifsc_code', 'branch'].includes(fieldId)) {
          value = emp.bank_details?.[fieldId];
        }

        if (fieldId === 'date_of_joining' || fieldId === 'date_of_birth') {
          value = value ? new Date(value).toLocaleDateString() : '-';
        }

        if (fieldId === 'work_locations' && Array.isArray(value)) {
          value = value.join(', ');
        }

        if (fieldId === 'emergency_contact' && typeof value === 'object' && value !== null) {
          value = `${value.name || ''}${value.relationship ? ` (${value.relationship})` : ''}${value.phone ? ` - ${value.phone}` : ''}`;
        }

        row[field.label] = value || '-';
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    // Auto-size columns
    const maxWidths = dataToExport.reduce((acc, row) => {
      Object.keys(row).forEach((key, i) => {
        const val = String(row[key]);
        acc[i] = Math.max(acc[i] || 0, val.length, key.length);
      });
      return acc;
    }, []);
    worksheet['!cols'] = maxWidths.map(w => ({ wch: w + 2 }));

    XLSX.writeFile(workbook, `employee_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    setIsExportOpen(false);
    toast.success('Excel downloaded successfully');
  };

  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-800 hover:bg-green-100',
    INACTIVE: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    ON_NOTICE: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
    TERMINATED: 'bg-red-100 text-red-800 hover:bg-red-100',
  };

  const columns = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px] ml-1"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px] ml-1"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title="Employee" />,
      cell: ({ row }) => {
        const emp = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs overflow-hidden">
              {emp.profile_picture ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${emp.profile_picture}`} alt="" className="h-full w-full object-cover" />
              ) : (
                <>{emp.first_name[0]}{emp.last_name[0]}</>
              )}
            </div>
            <div>
              <p className="font-bold text-slate-900 leading-none">{emp.first_name} {emp.last_name}</p>
              <p className="text-[11px] text-muted-foreground mt-1 font-medium">{emp.email}</p>
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'employee_code',
      header: 'Code',
      className: 'font-mono text-xs font-bold text-slate-500'
    },
    {
      accessorKey: 'department',
      header: 'Department',
      cell: ({ row }) => <span className="text-sm font-medium text-slate-600">{row.original.department || '-'}</span>
    },
    {
      accessorKey: 'designation',
      header: 'Designation',
      cell: ({ row }) => <span className="text-sm font-medium text-slate-600">{row.original.designation || '-'}</span>
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge className={cn("text-[10px] font-bold px-2 py-0.5 border-none", statusColors[row.original.status])}>
          {row.original.status?.replace('_', ' ')}
        </Badge>
      )
    },
    {
      accessorKey: 'date_of_joining',
      header: ({ column }) => <SortableHeader column={column} title="Joined" />,
      cell: ({ row }) => (
        <span className="text-xs font-bold text-slate-500 uppercase tracking-tighter">
          {new Date(row.original.date_of_joining).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      className: 'text-right',
      cell: ({ row }) => {
        const emp = row.original;
        return (
          <div className="flex justify-end gap-1.5">
            <Link to={`/employees/${emp.id}`}>
              <Button variant="outline" size="icon" className="h-8 w-8 border-slate-200">
                <Eye className="h-4 w-4 text-slate-500" />
              </Button>
            </Link>
            {emp.status === 'ACTIVE' ? (
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-slate-200 text-destructive hover:bg-destructive/5"
                onClick={(e) => { e.stopPropagation(); handleDeactivate(emp.id); }}
                title="Deactivate"
              >
                <UserX className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 border-slate-200 text-green-600 hover:bg-green-50"
                onClick={(e) => { e.stopPropagation(); handleReactivate(emp.id); }}
                title="Reactivate"
              >
                <RefreshCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ModuleDisabled moduleName="employees" />;
  }

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Employees</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Manage your organization's employees</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setIsBulkUploadOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Bulk Upload
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
                <DialogDescription>
                  Enter the employee details to add them to your organization
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateEmployee}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="employee_code">Employee Code *</Label>
                      <Input
                        id="employee_code"
                        value={formData.employee_code}
                        onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                        required
                        data-testid="employee-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="employee-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        data-testid="employee-first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        data-testid="employee-last-name-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date_of_joining">Date of Joining *</Label>
                      <Input
                        id="date_of_joining"
                        type="date"
                        value={formData.date_of_joining}
                        onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                        required
                        data-testid="employee-doj-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="department">Department</Label>
                      <Select
                        value={formData.department}
                        onValueChange={(value) => setFormData({ ...formData, department: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Department" />
                        </SelectTrigger>
                        <SelectContent>
                          {deptOptions.map((dept) => (
                            <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="designation">Designation</Label>
                      <Select
                        value={formData.designation}
                        onValueChange={(value) => setFormData({ ...formData, designation: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Designation" />
                        </SelectTrigger>
                        <SelectContent>
                          {desgOptions
                            .filter(desg => !formData.department || !desg.department_name || desg.department_name === formData.department)
                            .map((desg) => (
                              <SelectItem key={desg.id} value={desg.name}>{desg.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="employment_type">Employment Type</Label>
                      <Select
                        value={formData.employment_type}
                        onValueChange={(value) => setFormData({ ...formData, employment_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FULL_TIME">Full Time</SelectItem>
                          <SelectItem value="PART_TIME">Part Time</SelectItem>
                          <SelectItem value="CONTRACT">Contract</SelectItem>
                          <SelectItem value="INTERN">Intern</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Work Locations</Label>
                      <MultiSelect
                        options={locations.map(loc => ({ label: loc.name, value: loc.name }))}
                        selected={formData.work_locations || []}
                        onChange={(values) => setFormData({ ...formData, work_locations: values })}
                        placeholder="Select locations..."
                      />
                    </div>
                  </div>
                  <div className="border-t pt-4 mt-2">
                    <h4 className="font-medium mb-3">Login Credentials (Optional)</h4>
                    <div className="space-y-2">
                      <Label htmlFor="user_password">Password</Label>
                      <Input
                        id="user_password"
                        type="password"
                        value={formData.user_password}
                        onChange={(e) => setFormData({ ...formData, user_password: e.target.value })}
                        placeholder="Leave empty to skip account creation"
                      />
                      <p className="text-xs text-muted-foreground">
                        If provided, a user account will be created for self-service access
                      </p>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createLoading} data-testid="submit-employee-btn">
                    {createLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Add Employee'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-employee-input"
            />
          </div>
          <Button type="submit" variant="secondary">Search</Button>
        </form>
        <Select value={department || '_all'} onValueChange={(v) => { setDepartment(v === '_all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Departments</SelectItem>
            {departments.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {Object.keys(rowSelection).length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center justify-between animate-in fade-in slide-in-from-top-2">
          <p className="text-sm font-medium text-primary">
            {Object.keys(rowSelection).length} employees selected
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-primary/20 hover:bg-primary/10 hover:text-primary flex items-center gap-2"
              onClick={handleDownloadExcel}
            >
              <Download className="h-3.5 w-3.5" />
              Download Excel
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setRowSelection({})}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Employees Table */}
      <DataTable
        columns={columns}
        data={employees}
        loading={loading}
        emptyMessage="No employees found matching your criteria."
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
      />

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} employees
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page * 20 >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ConfirmationDialog {...dialogProps} />

      <BulkUploadModal
        open={isBulkUploadOpen}
        onOpenChange={setIsBulkUploadOpen}
        onSuccess={fetchEmployees}
        companyId={user?.company_id}
      />

      <Dialog open={isExportOpen} onOpenChange={setIsExportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl">Export Employee Data</DialogTitle>
                <DialogDescription>
                  Select the fields you want to include in the Excel export for {Object.keys(rowSelection).length} employees.
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedExportFields(EXPORT_FIELDS.map(f => f.id))}
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedExportFields([])}
                >
                  Clear All
                </Button>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {['Basic', 'Employment', 'Personal', 'Financial'].map(category => (
                <div key={category} className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm text-slate-900 uppercase tracking-wider">{category}</h3>
                    <Badge variant="secondary" className="text-[10px] font-bold h-4 px-1.5 leading-none">
                      {EXPORT_FIELDS.filter(f => f.category === category).length}
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    {EXPORT_FIELDS.filter(f => f.category === category).map(field => (
                      <div key={field.id} className="flex items-center space-x-2 group cursor-pointer" onClick={() => {
                        setSelectedExportFields(prev =>
                          prev.includes(field.id) ? prev.filter(id => id !== field.id) : [...prev, field.id]
                        );
                      }}>
                        <Checkbox
                          id={`export-${field.id}`}
                          checked={selectedExportFields.includes(field.id)}
                          onCheckedChange={(checked) => {
                            setSelectedExportFields(prev =>
                              checked ? [...prev, field.id] : prev.filter(id => id !== field.id)
                            );
                          }}
                        />
                        <label
                          htmlFor={`export-${field.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer group-hover:text-primary transition-colors"
                        >
                          {field.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <Separator />

          <DialogFooter className="p-6 bg-slate-50/50">
            <div className="flex-1 flex items-center">
              <p className="text-xs text-muted-foreground font-medium">
                {selectedExportFields.length} out of {EXPORT_FIELDS.length} fields selected
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsExportOpen(false)}>
                Cancel
              </Button>
              <Button onClick={performExport} className="min-w-[120px]">
                <Download className="mr-2 h-4 w-4" />
                Export Excel
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
